<template>
  <div class="ai">
    <div v-if="loading" class="ps-muted">Lade Einstellungen …</div>
    <template v-else>
      <p class="ai__intro">
        Live-Tasks, deren Upscale bestanden hat, werden nach einer zufälligen Wartezeit automatisch importiert, inklusive
        Vorschau-Mail an den Kunden. Importiert wird nur zu den Arbeitszeiten. Nie automatisch: geflaggte Tasks, Order-Note,
        Nachbestellungen, offenes Support-Ticket, Hold, Fallback nach fehlgeschlagenem Upscale, Zeichen ohne Schrift.
      </p>

      <!-- Notschalter: wirkt sofort, ohne „Speichern" -->
      <v-switch v-model="form.enabled" inset hide-details class="mt-0 mb-4" :label="form.enabled ? 'Auto-Import ist an' : 'Auto-Import ist aus'" @change="saveFlag('enabled')" />

      <!-- Überarbeitete Versionen: wirkt wie der Notschalter sofort -->
      <v-switch
          v-model="form.includeRevised"
          inset
          hide-details
          class="mt-0 mb-1"
          label="Auch überarbeitete Versionen automatisch importieren"
          @change="saveFlag('includeRevised')"
      />
      <p class="ai__hint">
        Überarbeitet = der Detail-Pass hat mindestens einen Bereich neu gerendert, zum Beispiel ein Gesicht von 60 auf 90.
        Aus: Diese Tasks bleiben zur Kontrolle unter „Manuell", auch wenn sie danach bestanden haben.
      </p>

      <div class="ai__grid">
        <div>
          <div class="ai__label">Wartezeit (Minuten)</div>
          <div class="ai__range">
            <v-text-field v-model.number="form.delay.min" type="number" min="0" dense outlined hide-details label="von" />
            <v-text-field v-model.number="form.delay.max" type="number" min="0" dense outlined hide-details label="bis" />
          </div>
        </div>
        <div>
          <div class="ai__label">Wartezeit Express (Minuten)</div>
          <div class="ai__range">
            <v-text-field v-model.number="form.expressDelay.min" type="number" min="0" dense outlined hide-details label="von" />
            <v-text-field v-model.number="form.expressDelay.max" type="number" min="0" dense outlined hide-details label="bis" />
          </div>
        </div>
      </div>

      <div class="ai__label mt-5">Arbeitszeiten</div>
      <div v-for="day in DAYS" :key="day.key" class="ai__day" :class="{ 'ai__day--off': !form.hours[day.key].active }">
        <v-checkbox v-model="form.hours[day.key].active" hide-details dense class="mt-0 pt-0 ai__dayName" :label="day.label" />
        <input v-model="form.hours[day.key].from" type="time" class="ai__time" :disabled="!form.hours[day.key].active" />
        <span class="ps-muted">bis</span>
        <input v-model="form.hours[day.key].to" type="time" class="ai__time" :disabled="!form.hours[day.key].active" />
      </div>

      <div class="ai__label mt-5">Ausgeschlossene Tage</div>
      <div class="d-flex align-center flex-wrap">
        <input v-model="newDate" type="date" class="ai__time mr-2" />
        <v-btn small depressed :disabled="!newDate" @click="addDate">Hinzufügen</v-btn>
      </div>
      <div class="mt-2">
        <v-chip v-for="date in form.excludedDates" :key="date" small close class="mr-2 mb-2" @click:close="removeDate(date)">{{ formatDate(date) }}</v-chip>
        <span v-if="!form.excludedDates.length" class="ps-muted">Keine</span>
      </div>

      <div class="d-flex align-center mt-5">
        <v-btn color="primary" depressed :loading="saving" :disabled="!dirty || !!validationError" @click="save">Speichern</v-btn>
        <span v-if="validationError" class="ml-3 red--text text-caption">{{ validationError }}</span>
        <span v-else-if="savedAt && !dirty" class="ml-3 ps-muted">Gespeichert</span>
      </div>
    </template>
  </div>
</template>

<script>
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../../../fb";

const DAYS = [
  { key: "mon", label: "Montag" },
  { key: "tue", label: "Dienstag" },
  { key: "wed", label: "Mittwoch" },
  { key: "thu", label: "Donnerstag" },
  { key: "fri", label: "Freitag" },
  { key: "sat", label: "Samstag" },
  { key: "sun", label: "Sonntag" },
];
const workday = (active) => ({ active, from: "08:00", to: "18:00" });
// Vorbelegung = DEFAULT_AUTO_IMPORT in firebase_aq/functions-upscaler/src/autoImport.ts
const defaults = () => ({
  enabled: true,
  includeRevised: false,
  delay: { min: 30, max: 90 },
  expressDelay: { min: 20, max: 30 },
  hours: { mon: workday(true), tue: workday(true), wed: workday(true), thu: workday(true), fri: workday(true), sat: workday(false), sun: workday(false) },
  excludedDates: [],
});

/** Einstellungen des Auto-Imports (settings/autoImport) — gelesen vom Server-Zeitplaner */
export default {
  name: "AutoImportPanel",
  data() {
    return { DAYS, loading: true, saving: false, savedAt: 0, form: defaults(), saved: "", newDate: "" };
  },
  computed: {
    dirty() {
      return JSON.stringify(this.form) !== this.saved;
    },
    validationError() {
      const f = this.form;
      for (const r of [f.delay, f.expressDelay]) {
        if (!(r.min >= 0) || !(r.max >= r.min)) return "Wartezeit: bis muss mindestens so groß sein wie von.";
      }
      for (const day of DAYS) {
        const h = f.hours[day.key];
        if (h.active && !(h.from && h.to && h.to > h.from)) return `${day.label}: Ende muss nach dem Beginn liegen.`;
      }
      return "";
    },
  },
  async created() {
    const snap = await getDoc(doc(db, "settings", "autoImport"));
    const s = snap.exists() ? snap.data() : {};
    const d = defaults();
    this.form = {
      enabled: s.enabled === undefined ? d.enabled : !!s.enabled,
      includeRevised: s.includeRevised === undefined ? d.includeRevised : !!s.includeRevised,
      delay: { ...d.delay, ...(s.delay || {}) },
      expressDelay: { ...d.expressDelay, ...(s.expressDelay || {}) },
      hours: Object.fromEntries(DAYS.map((day) => [day.key, { ...d.hours[day.key], ...((s.hours && s.hours[day.key]) || {}) }])),
      excludedDates: Array.isArray(s.excludedDates) ? [...s.excludedDates].sort() : [],
    };
    this.saved = JSON.stringify(this.form);
    this.loading = false;
  },
  methods: {
    formatDate(date) {
      return new Date(date + "T12:00:00").toLocaleDateString([], { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
    },
    addDate() {
      if (this.newDate && !this.form.excludedDates.includes(this.newDate)) this.form.excludedDates = [...this.form.excludedDates, this.newDate].sort();
      this.newDate = "";
    },
    removeDate(date) {
      this.form.excludedDates = this.form.excludedDates.filter((d) => d !== date);
    },
    /** Schalter sofort schreiben — der Rest erst mit „Speichern" */
    async saveFlag(key) {
      await setDoc(doc(db, "settings", "autoImport"), { [key]: this.form[key] }, { merge: true });
      const saved = this.saved ? JSON.parse(this.saved) : {};
      this.saved = JSON.stringify({ ...this.form, ...saved, [key]: this.form[key] });
    },
    async save() {
      this.saving = true;
      try {
        await setDoc(doc(db, "settings", "autoImport"), JSON.parse(JSON.stringify(this.form)));
        this.saved = JSON.stringify(this.form);
        this.savedAt = Date.now();
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ai {
  max-width: 760px;

  &__intro {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
    margin: 6px 0 14px;
  }
  &__hint {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.55);
    margin: 0 0 16px 58px;
  }
  &__label {
    font-size: 12px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.6);
    margin-bottom: 6px;
  }
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }
  &__range {
    display: flex;
    gap: 8px;
  }
  &__day {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 0;

    &--off {
      opacity: 0.55;
    }
  }
  &__dayName {
    width: 140px;
    flex: none;
  }
  &__time {
    padding: 5px 8px;
    border: 1px solid rgba(0, 0, 0, 0.25);
    border-radius: 6px;
    font-size: 13px;
    font-variant-numeric: tabular-nums;

    &:disabled {
      background: rgba(0, 0, 0, 0.04);
    }
  }
}
</style>
